import { useState, useEffect, useCallback } from "react";
import { Lock, Plus, X, Star, Play, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { errText, BACKEND_URL } from "@/lib/api";
import { CoinIcon } from "@/components/Icons";
import { useAuth } from "@/context/AuthContext";

const mediaUrl = (url) => (url?.startsWith("/") ? `${BACKEND_URL}${url}` : url);

export default function PostsSection({ targetId, isMe, targetName }) {
  const { setUser } = useAuth();
  const [data, setData] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ caption: "", is_exclusive: false, file: null });
  const [busy, setBusy] = useState(false);
  const [fanPrice, setFanPrice] = useState("");

  const load = useCallback(() => {
    api.get(`/users/${targetId}/posts`).then(({ data: d }) => {
      setData(d);
      setFanPrice(d.fan_price);
    }).catch(() => {});
  }, [targetId]);

  useEffect(() => { load(); }, [load]);

  const joinFans = async () => {
    try {
      const { data: res } = await api.post(`/users/${targetId}/fan/join`);
      setUser((u) => ({ ...u, coins: res.coins }));
      toast.success(`You're now a fan of ${targetName}! ⭐ Exclusive content unlocked`);
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const createPost = async (e) => {
    e.preventDefault();
    if (!form.file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("caption", form.caption);
      fd.append("is_exclusive", form.is_exclusive);
      fd.append("file", form.file);
      await api.post("/posts", fd);
      toast.success("Post published!");
      setShowCreate(false);
      setForm({ caption: "", is_exclusive: false, file: null });
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const deletePost = async (p) => {
    try {
      await api.delete(`/posts/${p.id}`);
      toast.success("Post deleted");
      setViewer(null);
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const saveFanPrice = async () => {
    try {
      await api.put("/fan/settings", { fan_price: Number(fanPrice) });
      toast.success("Fan club price updated");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  if (!data) return <div className="grid grid-cols-3 gap-2 mt-8">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="skeleton aspect-[3/4] rounded-2xl" />)}</div>;

  const hasLocked = data.posts.some((p) => p.locked);

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-bold text-zinc-200 font-display">📸 Posts {data.fan_count > 0 && <span className="text-[10px] text-zinc-500 font-semibold ml-1">· {data.fan_count} fan{data.fan_count > 1 ? "s" : ""}</span>}</h2>
        {isMe && (
          <button data-testid="new-post-button" onClick={() => setShowCreate(true)}
            className="gradient-primary text-white text-[10px] font-bold px-3 py-1.5 rounded-full flex items-center gap-1"><Plus size={12} /> New Post</button>
        )}
      </div>

      {!isMe && data.is_fan && (
        <div className="glass border-amber-400/50 rounded-2xl px-4 py-2.5 mb-3 flex items-center gap-2" data-testid="fan-badge">
          <Star size={14} className="text-amber-400" />
          <p className="text-xs text-amber-300 font-semibold">You're a fan! Membership until {new Date(data.fan_expires_at).toLocaleDateString()}</p>
        </div>
      )}

      {!isMe && !data.is_fan && hasLocked && (
        <button data-testid="join-fans-button" onClick={joinFans}
          className="w-full gradient-primary text-white font-bold py-3.5 rounded-2xl mb-3 flex items-center justify-center gap-2 active:scale-[0.98] transition-transform shadow-[0_0_20px_rgba(236,72,153,0.35)]">
          <Star size={16} /> Join Fan Club · <CoinIcon size={13} /> {data.fan_price.toLocaleString()}/month
        </button>
      )}

      <div className="grid grid-cols-3 gap-2" data-testid="posts-grid">
        {data.posts.length === 0 && <p className="col-span-3 text-zinc-500 text-xs text-center py-6">No posts yet</p>}
        {data.posts.map((p) => (
          <button key={p.id} data-testid={`post-${p.id}`}
            onClick={() => (p.locked ? joinPrompt(p) : setViewer(p))}
            className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-[#131A2A] group">
            {p.locked ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5"
                style={{ background: "linear-gradient(135deg, #7C3AED22, #EC489922)" }} data-testid={`post-locked-${p.id}`}>
                <Lock size={20} className="text-zinc-400" />
                <span className="text-[9px] text-zinc-400 font-bold">Fans only</span>
              </div>
            ) : p.type === "video" ? (
              <>
                <video src={mediaUrl(p.url)} muted playsInline className="absolute inset-0 w-full h-full object-cover" />
                <span className="absolute inset-0 flex items-center justify-center"><Play size={22} className="text-white drop-shadow" fill="white" /></span>
              </>
            ) : (
              <img src={mediaUrl(p.url)} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
            )}
            {p.is_exclusive && !p.locked && (
              <span className="absolute top-1.5 right-1.5 bg-amber-400 text-black text-[8px] font-bold px-1.5 py-0.5 rounded-full">⭐ FAN</span>
            )}
          </button>
        ))}
      </div>

      {isMe && (
        <div className="glass rounded-2xl p-4 mt-4" data-testid="fan-price-settings">
          <p className="text-xs font-semibold text-zinc-200 mb-2">⭐ Fan Club price (coins / month)</p>
          <div className="flex gap-2">
            <input data-testid="fan-price-input" type="number" min={10} value={fanPrice} onChange={(e) => setFanPrice(e.target.value)}
              className="flex-1 bg-[#131A2A] border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-violet-500" />
            <button data-testid="fan-price-save" onClick={saveFanPrice} className="gradient-primary text-white text-xs font-bold px-4 rounded-xl">Save</button>
          </div>
        </div>
      )}

      {/* Media viewer */}
      {viewer && (
        <div className="fixed inset-0 z-[11000] bg-black/95 flex flex-col items-center justify-center p-4" onClick={() => setViewer(null)} data-testid="post-viewer">
          <button data-testid="post-viewer-close" className="absolute top-4 right-4 glass-dark rounded-full w-9 h-9 flex items-center justify-center text-white"><X size={18} /></button>
          <div className="max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            {viewer.type === "video" ? (
              <video src={mediaUrl(viewer.url)} controls autoPlay loop playsInline className="w-full max-h-[70vh] rounded-2xl object-contain bg-black" />
            ) : (
              <img src={mediaUrl(viewer.url)} alt="" className="w-full max-h-[70vh] rounded-2xl object-contain" />
            )}
            <div className="flex items-center justify-between mt-3">
              <p className="text-white text-sm">{viewer.caption} {viewer.is_exclusive && <span className="text-amber-400 text-[10px] font-bold ml-1">⭐ FAN EXCLUSIVE</span>}</p>
              {isMe && (
                <button data-testid="post-delete-button" onClick={() => deletePost(viewer)} className="text-rose-400 p-2"><Trash2 size={16} /></button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create post modal */}
      {showCreate && (
        <div className="fixed inset-0 z-[11000] bg-black/85 flex items-center justify-center p-5">
          <form onSubmit={createPost} className="glass rounded-3xl p-6 w-full max-w-sm">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-bold font-display text-white">New Post</h2>
              <button type="button" data-testid="create-post-close" onClick={() => setShowCreate(false)}><X size={18} className="text-zinc-400" /></button>
            </div>
            <input data-testid="post-file-input" type="file" accept="image/*,video/*" required
              onChange={(e) => setForm((f) => ({ ...f, file: e.target.files?.[0] }))}
              className="w-full text-xs text-zinc-400 mb-3 file:bg-white/10 file:border-0 file:rounded-lg file:px-3 file:py-2 file:text-zinc-200 file:mr-2" />
            <input data-testid="post-caption-input" value={form.caption} onChange={(e) => setForm((f) => ({ ...f, caption: e.target.value }))}
              placeholder="Caption…" className="w-full bg-[#131A2A] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none mb-3 focus:border-violet-500 text-white" />
            <label className="flex items-center gap-2 text-xs text-zinc-200 mb-4 cursor-pointer">
              <input data-testid="post-exclusive-checkbox" type="checkbox" checked={form.is_exclusive}
                onChange={(e) => setForm((f) => ({ ...f, is_exclusive: e.target.checked }))} className="accent-pink-500" />
              ⭐ Fan-exclusive (only paying fans can view)
            </label>
            <button data-testid="post-submit-button" disabled={!form.file || busy}
              className="w-full gradient-primary text-white font-bold py-3 rounded-xl disabled:opacity-40">
              {busy ? "Uploading…" : "Publish"}
            </button>
          </form>
        </div>
      )}
    </div>
  );

  function joinPrompt() {
    if (data.is_fan) return;
    toast(`⭐ Fan-exclusive content — join ${targetName}'s fan club for ${data.fan_price} coins/month`, {
      action: { label: "Join", onClick: joinFans },
    });
  }
}
